import 'react-app-polyfill/ie11';
import React, { useState, useEffect, useRef } from 'react';
import __ from './internetLanguage';
import config from './internetConfig';
import utils from '../utils';
import cache from '../services/cache';
import datepicker from 'js-datepicker';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import solrInstances from './solrInstances';
import metrics from './internetMetrics';


export default function Form(props) {
	const form = props.form;
	const data = form.data;
	const $ = config.isBrowser ? window.jQuery : false;
	const [typeAheadResults, setTypeAheadResults] = useState([]);
	const [advancedExpanded, setAdvancedExpanded] = useState(false);
	const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
	const typeaheadSolr = solrInstances.en.typeahead;
	const [datepickers, setDatepickers] = useState([]);
	const typeaheadRef = useRef(null);
	const searchFormRef = useRef(null);
	const advanceSearchRef = useRef(null);

	// Datepicker references
	const dateRefs = [React.useRef(null), React.useRef(null)];

	const languageOptions = [
		{ value: '', label: __('anyLanguage') },
		{ value: 'en', label: __('langen') },
		{ value: 'es', label: __('langes') },
		{ value: 'fr', label: __('langfr') },
		{ value: 'de', label: __('langde') },
		{ value: 'zh', label: __('langzh') },
		{ value: 'ko', label: __('langko') },
		{ value: 'vi', label: __('langvi') },
		{ value: 'ja', label: __('langja') },
		{ value: 'ru', label: __('langru') },
		{ value: 'pt', label: __('langpt') },
		{ value: 'it', label: __('langit') },
	];

	// Toggle language dropdown
	const toggleLanguageDropdown = () => {
		setLanguageDropdownOpen(!languageDropdownOpen);
	};

	// Handle language dropdown selection
	const handleLanguageDropdown = (value) => {
		form.setLanguage(value);
		setLanguageDropdownOpen(false);
	};

	// Get current language label
	const getCurrentLanguageLabel = () => {
		const currentOption = languageOptions.find(opt => opt.value === data.language);
		return currentOption ? currentOption.label : __('anyLanguage');
	};

	// Clear only advanced fields
	const advancedReset = () => {
		form.setAny('');
		form.setAll('');
		form.setExact('');
		form.setNone('');
		form.setUrl('');
		form.setLanguage('');
		form.setDate1('');
		form.setDate2('');
		form.setStrict('');
	}

	// Pressing enter inside an input
	const onEnter = (e) => {
		if ('Enter' === e.key) {
			form.doSearch(true);
			setTypeAheadResults([]);
		}
	};

	// Typeahead select
	const selectTypeahead = (result) => {
		data.query = result;
		form.setQuery(result);
		form.doSearch(true);
		setTypeAheadResults([]);
	};

	// Clear query
	const clearQuery = (e) => {
		form.setQuery('');
		if (typeaheadRef.current) {
			typeaheadRef.current.clear();
		}
	}

	const openDate = (d) => {
		dateRefs[d].current.focus();
		setTimeout(() => {
			datepickers[d].show();
		}, 1);
	};

	const highlightMatch = (text, query) => {
		if (!query) return text;

		const regex = new RegExp(`(${query})`, 'gi');
		const parts = text.split(regex);

		return parts.map((part, idx) =>
			part.toLowerCase() === query.toLowerCase() ? (
				<strong key={idx}>{part}</strong>
			) : (
				<span key={idx}>{part}</span>
			)
		);
	};

	const getTypeAhead = () => {
		const newQuery = typeaheadRef.current.getInput().value;
		typeaheadSolr.query({ query: newQuery }).then((response) => {
			setTypeAheadResults(response.results);
		})
	}

	const onTypeAhead = utils.debounce(() => getTypeAhead(), 600);

	// When query field is changed
	const onQueryChange = (newQuery) => {
		form.setQuery(newQuery);
		if (
			newQuery &&
			newQuery.length >= config.typeahead.charsMin &&
			(!config.typeahead.charsMax || newQuery.length < config.typeahead.charsMax )
		) {
			onTypeAhead();
		}
	};

	// Advanced Form toggle
	const toggleAdvanced = (e) => {
		if (e) {
			if (!advancedExpanded && !cache.advancedExpanded) {
				metrics.event(e, 'cdcsitesearch-advanced-open');
				cache.advancedExpanded = true;
			}
			e.preventDefault();
		}
		if ($?.fn?.collapse) {
			if (advancedExpanded) {
				$('#advanced').collapse('hide');
			} else {
				$('#advanced').collapse('show');
			}
		}
		setAdvancedExpanded(!advancedExpanded);
	};

	// initialize
	useEffect(() => {

		// initialize datepickers
		if (config.isEnglish) {
			let pickers = [];
			pickers.push(
				datepicker('#search-date1', {
					customDays: ['S', 'M', 'T', 'W', 'R', 'F', 'S'],
					onSelect: (instance, date) => {
						form.setDateRange('');
						form.setDate1(utils.formatDate(date));
					},
					maxDate: new Date(),
				})
			);
			pickers.push(
				datepicker('#search-date2', {
					customDays: ['S', 'M', 'T', 'W', 'R', 'F', 'S'],
					onSelect: (instance, date) => {
						form.setDateRange('');
						form.setDate2(utils.formatDate(date));
					},
					maxDate: new Date(),
				})
			);
			setDatepickers(pickers);
		}
		// on load, auto expand advanced form if #advanced
		if (config.isBrowser && '#advanced' === window.location.hash) {
			toggleAdvanced();
		}
	}, []);

	useEffect(() => {
		if (datepickers.length) {
			let dates = [data.date1 ? utils.toDate(data.date1) : false, data.date2 ? utils.toDate(data.date2) : false];
			dates.forEach((date, index) => {
				if (date) {
					datepickers[index].navigate(date, true);
				}
			});
		}
	}, [data.date1, data.date2]);

	useEffect(() => {
		if (typeaheadRef?.current && data.query !== typeaheadRef.current.getInput().value) {
			typeaheadRef.current.setState({
				text: data.query,
			})
		}
	}, [data.query])

	useEffect(() => {
		const addFocusVisible = (element) => () => element && element.classList.add('focus-visible');
		const removeFocusVisibleOnTab = (element) => (event) => {
			if (event.key === 'Tab' && element) {
				element.classList.remove('focus-visible');
			}
		};

		const elements = [
			{ ref: searchFormRef, name: 'searchForm' },
			{ ref: advanceSearchRef, name: 'advanceSearch' }
		];

		// Store handlers in an array for cleanup
		const handlers = [];

		elements.forEach(({ ref }) => {
			const el = ref.current;
			if (el) {
				const handleMouseDown = addFocusVisible(el);
				const handleKeyDown = removeFocusVisibleOnTab(el);
				el.addEventListener('mousedown', handleMouseDown);
				document.body.addEventListener('keydown', handleKeyDown);

				handlers.push({
					element: el,
					mouseHandler: handleMouseDown,
					keyHandler: handleKeyDown
				});
			}
		});

		return () => {
			handlers.forEach(({ element, mouseHandler, keyHandler }) => {
				if (element) {
					element.removeEventListener('mousedown', mouseHandler);
					document.body.removeEventListener('keydown', keyHandler);
				}
			});
		};
	}, [searchFormRef, advanceSearchRef]);

	return (
		<div className="search-form">
			<div className="row search-form__basic" ref={searchFormRef}>
				<div className="col-lg input-group input-group__search">
					{config.isEnglish && config.typeahead.enabled ? (
						<Typeahead
							className="form-control"
							maxLength="300"
							ref={typeaheadRef}
							onInputChange={(text) => onQueryChange(text)}
							onKeyDown={(e) => {
								if (e && 'Enter' === e.key) {
									let text = e.currentTarget?.value;
									if (text) {
										return selectTypeahead(text);
									} else {
										return onEnter(e);
									}
								}
							}}
							options={typeAheadResults || []}
							minLength={config.typeahead.threshhold}
							maxResults={config.typeahead.max}
							paginate={false}
							autoComplete="off"
							placeholder={__.ucFirst('search')}
							aria-label={__.ucFirst('search')}
							id="type-ahead"
							name="query"
							emptyLabel=""
							value={data.query}
							defaultInputValue={data.query}
							filterBy={() => true}
							renderMenu={(results, menuProps) =>
								results.length ? (
									<Menu {...menuProps}>
										{results.map((result, index) => (
											<MenuItem
												onClick={() => selectTypeahead(result)}
												option={result}
												key={index}
												position={index}
											>
												{highlightMatch(result, data.query)}
											</MenuItem>
										))}
									</Menu>
								) : null
							}
						/>
					) : (
						<input
							type="text"
							className="form-control"
							maxLength="300"
							autoComplete="off"
							placeholder={__.ucFirst('search')}
							aria-label={__.ucFirst('search')}
							name="query"
							value={data.query}
							onChange={(e) => form.setQuery(e.target.value)}
							onKeyPress={(e) => onEnter(e)}
						/>
					)}
					<div className="input-group-append cdc-search-button__container">
						<button
							onClick={clearQuery}
							className={`${data.query.length > 0 ? 'show' : 'hide'} btn form-control-clear`}
							aria-label={__('clear')}
						>
							<i className="cdc-fa-xmark" aria-label="close search"></i>
						</button>
						<button className="btn search-submit" aria-label={__('search') } type="button" onClick={(e) => form.doSearch(true)}>
							<i className="cdc-fa-magnifying-glass" aria-hidden="true"></i>
						</button>
					</div>
				</div>
				<div className="col-lg advanced-btn">
					<a
						href="#advanced"
						data-toggle="collapse"
						aria-expanded={advancedExpanded}
						onClick={(e) => toggleAdvanced(e)}
					>
						{__('advancedSearch')}
						<i className="cdc-fa-angle-down"></i>
					</a>
				</div>
			</div>
			<div className="collapse advanced-search" id="advanced" ref={advanceSearchRef}>
				<div className="row mb-2 advanced-search__option-container">
					<div className="col-md col-label advanced-search__label">
						<label htmlFor="search-all">{__('formAll')}:</label>
					</div>
					<div className="col-md advanced-search__input-container">
						<input
							className="form-control"
							type="text"
							maxLength="100"
							id="search-all"
							value={data.all}
							onChange={(e) => form.setAll(e.target.value)}
							onKeyPress={(e) => onEnter(e)}
						/>
					</div>
				</div>
				<div className="row mb-2 advanced-search__option-container">
					<div className="col-md col-label advanced-search__label">
						<label htmlFor="search-exact">{__('formExact')}:</label>
					</div>
					<div className="col-md advanced-search__input-container">
						<input
							className="form-control"
							type="text"
							maxLength="100"
							id="search-exact"
							value={data.exact}
							onChange={(e) => form.setExact(e.target.value)}
							onKeyPress={(e) => onEnter(e)}
						/>
					</div>
				</div>
				<div className="row mb-2 advanced-search__option-container">
					<div className="col-md col-label advanced-search__label">
						<label htmlFor="search-any">{__('formAny')}:</label>
					</div>
					<div className="col-md advanced-search__input-container">
						<input
							className="form-control"
							type="text"
							maxLength="100"
							id="search-any"
							value={data.any}
							onChange={(e) => form.setAny(e.target.value)}
							onKeyPress={(e) => onEnter(e)}
						/>
					</div>
				</div>
				<div className="row mb-2 advanced-search__option-container">
					<div className="col-md col-label advanced-search__label">
						<label htmlFor="search-none">{__('formNone')}:</label>
					</div>
					<div className="col-md advanced-search__input-container">
						<input
							className="form-control"
							type="text"
							maxLength="100"
							id="search-none"
							value={data.none}
							onChange={(e) => form.setNone(e.target.value)}
							onKeyPress={(e) => onEnter(e)}
						/>
					</div>
				</div>
				<div className="row mb-2 advanced-search__option-container">
					<div className="col-md col-label advanced-search__label">
						<label htmlFor="search-url">{__('url')}:</label>
					</div>
					<div className="col-md advanced-search__input-container">
						<input
							className="form-control"
							type="text"
							maxLength="100"
							id="search-url"
							value={data.url}
							onChange={(e) => form.setUrl(e.target.value)}
							onKeyPress={(e) => onEnter(e)}
						/>
					</div>
				</div>
				<div className="row mb-2 advanced-search__option-container">
					<div className="col-md col-label advanced-search__label">
						<label htmlFor="search-language">{__('language')}:</label>
					</div>
					<div className="col-md advanced-search__input-container">
						<div className={`search-widget-dropdown ${languageDropdownOpen ? 'show' : ''}`}>
							<button
								id="search-language"
								className="search-widget-dropdown__button"
								onClick={toggleLanguageDropdown}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										toggleLanguageDropdown();
									}
								}}
								aria-expanded={languageDropdownOpen}
								aria-haspopup="listbox"
								type="button"
							>
								<span className="search-widget-dropdown__text">{getCurrentLanguageLabel()}</span>
							</button>
							{languageDropdownOpen && (
								<div className="search-widget-dropdown__list" role="listbox">
									{languageOptions.map((option) => (
										<button
											key={option.value}
											className="search-widget-dropdown__option"
											onClick={(e) => {
												e.stopPropagation();
												handleLanguageDropdown(option.value);
											}}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													e.stopPropagation();
													handleLanguageDropdown(option.value);
												}
											}}
											role="option"
											type="button"
										>
											{option.label}
										</button>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
				{config.isEnglish && (
					<div className='advanced-search__date-range'>
						<div className="row mb-2 advanced-search__option-container">
							<div className="col-md col-label advanced-search__label">
								<label htmlFor="search-date1">Between these dates:</label>
							</div>
							<div className="col-md mb-2 datepicker input-group input-group__date">
								<input
									className="form-control"
									ref={dateRefs[0]}
									type="text"
									title="From date"
									id="search-date1"
									placeholder="From"
									value={data.date1}
									onChange={(e) => form.setDate1(e.target.value)}
								/>
								<div className="input-group-append calendar_container">
									<button
										type="button"
										className="btn calendar_button"
										aria-label="open calendar"
										onClick={(e) => openDate(0)}
									>
										<span className="fi cdc-fa-calendar-day" aria-hidden="true"></span>
									</button>
								</div>
							</div>
							<div className="col-md mb-2 datepicker input-group input-group__date">
								<input
									className="form-control"
									ref={dateRefs[1]}
									type="text"
									title="To date"
									id="search-date2"
									placeholder="To"
									value={data.date2}
									onChange={(e) => form.setDate2(e.target.value)}
								/>
								<div className="input-group-append calendar_container">
									<button
										type="button"
										className="btn calendar_button"
										aria-label="open calendar"
										onClick={(e) => openDate(1)}
									>
										<span className="fi cdc-fa-calendar-day" aria-hidden="true"></span>
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				<div className="row">
					<div className="col-md-12 advanced-search__buttons">
						<button
							className="btn advanced-search__reset"
							type="button"
							id="search-clear"
							onClick={(e) => advancedReset()}
						>
							{__.ucFirst('clearAll')}
						</button>
						<button
							className="btn advanced-search__submit"
							type="button"
							id="search-advanced"
							onClick={(e) => form.doSearch()}
						>
							{__.ucFirst('apply')}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
